import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtEUR, monthLabel } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/components/hr/hrUtils";
import { MonthKpis, ModesCard, PendingCard, RetroCard } from "@/components/praticien/PratWidgets";
import { DaysCard, PatientsCard } from "@/components/praticien/PratLists";

function Evolution({ series }) {
  return (
    <div className={`${CARD} p-5 sm:p-6`}>
      <Label className="mb-3">Évolution sur 12 mois</Label>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
            <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#065F46" stopOpacity={0.25} /><stop offset="100%" stopColor="#065F46" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
            <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtEUR(v)} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
            <Area type="monotone" dataKey="total" stroke="#065F46" strokeWidth={2.5} fill="url(#cg)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MonthNav({ ym, setYm }) {
  const shift = (n) => { const d = new Date(ym.y, ym.m - 1 + n, 1); setYm({ y: d.getFullYear(), m: d.getMonth() + 1 }); };
  return (
    <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1">
      <button type="button" data-testid="month-prev" onClick={() => shift(-1)} className="rounded-full p-2 hover:bg-gray-50" aria-label="Mois précédent"><ChevronLeft className="h-4 w-4" /></button>
      <span data-testid="month-label" className="min-w-[8.5rem] text-center text-sm font-semibold">{monthLabel(ym.y, ym.m)}</span>
      <button type="button" data-testid="month-next" onClick={() => shift(1)} className="rounded-full p-2 hover:bg-gray-50" aria-label="Mois suivant"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

export default function PraticienCA() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [overview, setOverview] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-revenue").then((r) => setOverview(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!overview?.practitioner_id) return;
    setMonth(null);
    api.get("/my-revenue/month", { params: { year: ym.y, month: ym.m } }).then((r) => setMonth(r.data));
  }, [overview, ym]);

  const exportXlsx = () => downloadBlob(`/my-revenue/export?year=${ym.y}&month=${ym.m}`, `mon-ca-${ym.y}-${String(ym.m).padStart(2, "0")}.xlsx`)
    .catch(() => toast.error("Export impossible"));

  if (loading) return <Loader />;
  if (!overview?.practitioner_id)
    return <EmptyState title="Profil praticien non lié" subtitle="Votre compte n'est associé à aucun praticien. Contactez le propriétaire du cabinet." />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Bonjour {overview.practitioner_name}</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Mon chiffre d'affaires</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthNav ym={ym} setYm={setYm} />
          <Button data-testid="export-month-btn" onClick={exportXlsx} variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Export Excel</Button>
        </div>
      </div>
      {!month ? <Loader /> : (
        <>
          <MonthKpis data={month} />
          <div className="grid gap-5 lg:grid-cols-2">
            <PendingCard data={month} />
            <RetroCard retro={month.retrocession} />
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <ModesCard modes={month.modes} total={month.kpis.total} />
            <Evolution series={overview.series} />
          </div>
          <DaysCard days={month.days} />
          <PatientsCard patients={month.patients} />
        </>
      )}
    </div>
  );
}
